import type { Meter } from '../theory/rhythm';

/**
 * Strumming patterns on an eighth-note grid.
 *
 * Tokens: `D` down, `U` up, `-` no stroke, `x` muted chunk. One token per eighth
 * (per eighth-note in 6/8), so a 4/4 pattern has eight tokens and reads left to
 * right as "1 & 2 & 3 & 4 &".
 */

export interface StrumPattern {
  id: string;
  name: string;
  meter: Meter;
  grid: string[];
  /** 0 straight; about 0.66 for a shuffle. */
  swing?: number;
  /** 1 easiest. Mirrors the chord-shape tiers. */
  tier: number;
  description: string;
}

const P = (
  id: string,
  name: string,
  meter: Meter,
  grid: string,
  tier: number,
  description: string,
  swing?: number,
): StrumPattern => ({
  id,
  name,
  meter,
  grid: grid.split(' '),
  tier,
  description,
  ...(swing !== undefined ? { swing } : {}),
});

const M44: Meter = { beats: 4, unit: 4 };
const M34: Meter = { beats: 3, unit: 4 };
const M68: Meter = { beats: 6, unit: 8 };

export const STRUM_PATTERNS: StrumPattern[] = [
  P('strum.d4', 'Four downs', M44, 'D - D - D - D -', 1, 'One down-strum on each beat.'),
  P(
    'strum.dudu',
    'Straight eighths',
    M44,
    'D U D U D U D U',
    1,
    'Down on the beat, up on the "and".',
  ),
  P(
    'strum.island',
    'Island strum',
    M44,
    'D - D U - U D U',
    2,
    'The classic ukulele pattern: skip the up after 1 and the down on 3.',
  ),
  P('strum.d-du', 'Folk', M44, 'D - D U D - D U', 2, 'Down, down-up, down, down-up.'),
  P('strum.waltz', 'Waltz', M34, 'D - D U D U', 2, 'A strong down on 1, then lighter strokes.'),
  P(
    'strum.calypso',
    'Calypso',
    M44,
    'D - D U - U D -',
    3,
    'Keep the hand moving through the missing strokes.',
  ),
  P(
    'strum.shuffle',
    'Shuffle',
    M44,
    'D - D U D - D U',
    3,
    'The folk pattern with a swung feel.',
    0.66,
  ),
  P(
    'strum.chunk',
    'Chunk',
    M44,
    'D - x U - U x U',
    4,
    'Mute the strings with the palm on the "x" strokes.',
  ),
  P('strum.68', 'Six-eight', M68, 'D - - D - U', 4, 'Two big beats per bar, each split in three.'),
  P(
    'strum.reggae',
    'Off-beat skank',
    M44,
    '- - U - - - U -',
    5,
    'Strokes only on the "and" of 2 and 4.',
  ),
];

const BY_ID = new Map(STRUM_PATTERNS.map((p) => [p.id, p]));

export function getStrum(id: string): StrumPattern {
  const pattern = BY_ID.get(id);
  if (pattern === undefined) throw new Error(`Unknown strum pattern: ${id}`);
  return pattern;
}

export function strumsForTier(maxTier: number, meter?: Meter): StrumPattern[] {
  return STRUM_PATTERNS.filter(
    (p) =>
      p.tier <= maxTier &&
      (meter === undefined || (p.meter.beats === meter.beats && p.meter.unit === meter.unit)),
  );
}

/** Beat positions (quarter-note beats) at which the pattern strikes the strings. */
export function strumOnsets(pattern: StrumPattern): number[] {
  const step = 0.5; // one eighth note
  const out: number[] = [];
  pattern.grid.forEach((token, i) => {
    if (token === 'D' || token === 'U' || token === 'x') out.push(i * step);
  });
  return out;
}

/** "D DU UDU" style label for showing the pattern to a learner. */
export function strumLabel(pattern: StrumPattern): string {
  return pattern.grid.map((t) => (t === '-' ? '·' : t)).join(' ');
}
